/**
 * ErrorBoundary — top-level safety net. Any component crash below this
 * boundary shows a recoverable fallback instead of white-screening the app.
 * Logs the error to console (and could pipe to Sentry/analytics later).
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, info);
  }

  reset = (): void => {
    this.setState({ error: null });
    // Hard reload as a last-resort recovery — clears in-memory state.
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.error === null) return this.props.children;

    return (
      <div className="min-h-screen grid place-items-center px-5 bg-ink-950 text-cream-100">
        <div className="max-w-md w-full rounded-3xl border border-ember-400/30 bg-ember-400/[0.04] p-8 text-center">
          <div className="text-6xl mb-4">💥</div>
          <h1 className="font-display text-2xl font-bold text-white mb-2">
            出错了
          </h1>
          <p className="text-sm text-cream-400 mb-1">
            刚才那一步崩了 — 我们已记录此错误。
          </p>
          <pre className="mt-4 max-h-40 overflow-auto text-[10px] text-left text-ember-200 bg-ink-900/60 border border-ember-400/20 rounded-xl p-3 font-mono whitespace-pre-wrap">
            {this.state.error.message}
          </pre>
          <button
            onClick={this.reset}
            className="mt-6 inline-flex items-center justify-center rounded-full h-11 px-5 font-arcade tracking-widest text-xs bg-gradient-to-br from-honey-400 to-ember-500 text-ink-950 active:scale-[0.98] transition-transform"
          >
            ↺ 刷新重试
          </button>
        </div>
      </div>
    );
  }
}
